<template>
  <VCard
    :class="{'shadow-none': isExtraSmall}"
    class="booking-summary-container border-radius-8"
    data-test="booking-summary-container"
  >
    <SBookingSummary
      v-if="!isExtraSmall"
      :selectedRides="selectedRides"
      :isCheckoutPage="isCheckoutPage"
    />
    <template v-if="isAuth">
      <VRow class="pl-4 pt-2 align-items-center">
        <VCol
          cols="1"
          class="pb-0 checkbox-col"
          align-self="start"
        >
          <ValidationProvider
            ref="agreement"
            v-slot="{ ariaInput }"
            name="agreement"
            :rules="{ required: { allowFalse: false } }"
            :allowFalse="false"
            mode="eager"
          >
            <VCheckbox
              v-bind="ariaInput"
              v-model="isAgree"
              data-test="agreementCheckbox"
              class="pb-0 mt-0 agreement-checkbox"
              @change="updateAgreement"
            />
          </ValidationProvider>
        </VCol>
        <VCol
          cols="10"
          class="py-0"
          data-test="agreement-text-container"
        >
          <span class="body-2" data-test="agreement-text-start"><span class="error--text" aria-hidden="true">* </span>{{ $t('checkout.agreement.iAmAgree')}}</span>
          <a 
            :href="privacyPolicyPageName" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="body-1 text-decoration-none secondary--text"
            data-test="privacy-policy-link"
          >
            {{ $t('checkout.agreement.privacyPolicy') }}
          </a>
          <span class="body-2">{{ $t('checkout.agreement.and')}}</span>
          <a 
            :href="termsAndConditionsPageName" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="body-1 text-decoration-none secondary--text"
            data-test="terms-conditions-link"
          >
            {{ $t('checkout.agreement.termsAndConditions') }}
          </a>
        </VCol>
      </VRow>
      <VRow
        v-if="!isAgreementValid && !isAgree"
        class="pl-4 pt-0"
        data-test="agreement-error-container"
      >
        <VCol 
          cols="12"
          class="pt-0"
        >
         <span class="subtitle-2 error--text" data-test="agreement-error-message">{{ $t('checkout.agreement.error') }}</span>
        </VCol>
      </VRow>
      <VRow v-if="getIsOpsZoneShouldBePrefilled" class="pl-4 pt-0 mt-0 align-items-center">
          <VCol
            cols="1"
            class="pb-0 checkbox-col"
            align-self="start"
          >
            <ValidationProvider
              ref="communeAgreement"
              v-slot="{ ariaInput }"
              name="communeAgreement"
              :rules="{ required: { allowFalse: false } }"
              :allowFalse="false"
              mode="eager"
            >
              <VCheckbox
                v-bind="ariaInput"
                v-model="isCommuneConfirmed"
                data-test="agreementCommuneCheckbox"
                class="pb-0 mt-0 agreement-checkbox"
                @change="updateCommuneAgreement"
              />
            </ValidationProvider>
          </VCol>
          <VCol
            cols="10"
            class="py-0"
            data-test="commune-confirmation-text"
          >
            <span class="body-2"><span class="error--text" aria-hidden="true">* </span>{{ $t('checkout.communeConfirmation', { opsZoneName: getPrefilledOpsZoneName })}}</span>
          </VCol>
        </VRow>
      <VRow
        v-if="getIsOpsZoneShouldBePrefilled && !isAgreementValid && !isCommuneConfirmed"
        class="pl-4 pt-0"
        data-test="commune-error-container"
      >
        <VCol 
          cols="12"
          class="pt-0"
        >
         <span class="subtitle-2 error--text" data-test="commune-confirmation-error">{{ $t('checkout.communeConfirmationError') }}</span>
        </VCol>
      </VRow>
      <VCardActions class="pt-3">
        <VBtn
          class="w-100 shadow-none"
          color="brandingPrimary"
          data-test="bookNowButton"
          dark
          @click="bookNow"
        >
          <span class="button" data-test="book-now-text">
            {{ $t('checkout.bookNow') }}
          </span>
        </VBtn>
      </VCardActions>
    </template>
  </VCard>
</template>

<script lang="ts">
export default {
  name: 'SCheckoutBookingSummary',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import SBookingSummary from '@slg/web-customer-shared/src/components/common/bookingSummary/SBookingSummary';
import { useStore } from '../../../composables';

const props = defineProps({
  isExtraSmall: {
    type: Boolean,
    default: false,
  },
  selectedRides: {
    type: Array,
    default: () => [],
  },
  isAuth: {
    type: Boolean,
    default: false,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  termsAndConditionsPageName: {
    type: String,
    default: null,
  },
  privacyPolicyPageName: {
    type: String,
    default: null,
  },
  isCheckoutPage: {
    type: Boolean,
    default: false,
  },
  getIsOpsZoneShouldBePrefilled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['bookNow']);

const store = useStore();

const isAgree = ref(false);
const isAgreementValid = ref(true);
const isCommuneConfirmed = ref(false);

const prefilledOpsZone = computed(() => store.getters['booking/getPrefilledOpsZone']);

const getPrefilledOpsZoneName = computed(() => prefilledOpsZone.value?.opsZoneName);

function bookNow() {
  isAgreementValid.value = props.getIsOpsZoneShouldBePrefilled ? (isAgree.value && isCommuneConfirmed.value) : isAgree.value;
  emit('bookNow');
}

function updateAgreement(isAgreeValue) {
  isAgree.value = isAgreeValue;
}

function updateCommuneAgreement(isCommuneConfirmedValue) {
  isCommuneConfirmed.value = isCommuneConfirmedValue;
}
</script>
<style lang="scss" scoped>
.checkbox-col {
  padding-top: 18px;
}
</style>
